import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS, LANGS } from "@/lib/translations";
import { useSiteContent } from "@/context/SiteContentContext";

const LangCtx = createContext(null);
const STORAGE_KEY = "gl_lang";

const initialLang = () => {
  if (typeof window === "undefined") return "pt";
  try {
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
    const browser = (window.navigator?.language || "pt").slice(0, 2).toLowerCase();
    if (TRANSLATIONS[browser]) return browser;
  } catch (_e) { /* ignore */ }
  return "pt";
};

const LangProviderInner = ({ children }) => {
  const { overrides } = useSiteContent();  // admin-editable content, overlays TRANSLATIONS

  // Pre-seed from localStorage so the first paint is already in the correct language.
  const [lang, setLangState] = useState(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!TRANSLATIONS[code]) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
    document.documentElement.lang = code;
  }, []);

  /**
   * t(key, vars?)
   *   Resolution order:
   *     1. admin override (overrides[key][lang])
   *     2. compiled translation for current lang
   *     3. compiled EN fallback
   *     4. the key itself (dev-friendly loud fallback)
   */
  const t = useCallback((key, vars = {}) => {
    const override = overrides?.[key]?.[lang];
    const table = TRANSLATIONS[lang] || TRANSLATIONS.en;
    let s = override || table[key] || TRANSLATIONS.en[key] || key;
    Object.keys(vars).forEach((k) => { s = s.replace(new RegExp(`{${k}}`, "g"), String(vars[k])); });
    return s;
  }, [lang, overrides]);

  const value = useMemo(() => ({ lang, setLang, t, langs: LANGS }), [lang, setLang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
};

/**
 * LanguageProvider MUST be wrapped by SiteContentProvider so the useT() hook
 * can read admin-managed overrides. If SiteContentProvider is missing the
 * component still renders (overrides will be an empty object).
 */
export const LanguageProvider = ({ children }) => <LangProviderInner>{children}</LangProviderInner>;

export const useT = () => useContext(LangCtx);

/**
 * Render a translation string with simple inline tags <accent>…</accent> and <link>…</link>.
 * Pass `{ link: <button onClick={...}>Sign in</button> }` to swap in custom JSX for the link.
 */
export const renderRich = (str, { accentClass = "text-[#C97A63]", link } = {}) => {
  // Parse <accent>…</accent>
  const parts = [];
  let rest = str;
  let i = 0;
  while (rest.length) {
    const a = rest.indexOf("<accent>");
    const l = rest.indexOf("<link>");
    const next = [a, l].filter((x) => x >= 0).sort((x, y) => x - y)[0];
    if (next === undefined) { parts.push(rest); break; }
    if (next > 0) parts.push(rest.slice(0, next));
    if (next === a) {
      const end = rest.indexOf("</accent>", a);
      const inner = rest.slice(a + "<accent>".length, end);
      parts.push(<em key={`a${i++}`} className={`not-italic ${accentClass}`}>{inner}</em>);
      rest = rest.slice(end + "</accent>".length);
    } else if (next === l) {
      const end = rest.indexOf("</link>", l);
      const inner = rest.slice(l + "<link>".length, end);
      parts.push(link ? <span key={`l${i++}`}>{link(inner)}</span> : inner);
      rest = rest.slice(end + "</link>".length);
    }
  }
  return parts;
};
